import React from 'react'
import * as d3 from 'd3'
import Module from './Module'
import ModuleUtils from './ModuleUtils'
import ConfiguratorGridUtils, { GridElement, GridElementState } from './ConfiguratorGridUtils'

type SelectedElement = {
  module: Module;
  gridElement: GridElement;
}

export type DeviceData = {
  imageData: string;
  configuration: any;
}

export default class ConfiguratorGrid {

  public gridSize: number

  public boxSize: number

  public svgWidth: number

  public svgHeight: number

  public svg: d3.Selection<SVGSVGElement, unknown, null, undefined>

  public svgGroup: d3.Selection<any, unknown, null, undefined>

  public gridState: GridElementState[][]

  public zoom: d3.ZoomBehavior<any, any>

  public maxZoom: number

  public defaultZoom: number

  public selectedModule: Module | null

  public onModuleSelected: (x: number, y: number, isRotatable: boolean) => void

  public onModuleUnSelected: () => void

  public closeContextMenu: () => void


  constructor(deviceRef: React.RefObject<any>,
    onModuleSelected: (x: number, y: number, isRotatable: boolean) => void,
    onModuleUnSelected: () => void,
    closeContextMenu: () => void,
    gridSize = 50,
    boxSize = 100,
    defaultZoom = 1,
    maxZoom = 1.5) {
    this.onModuleSelected = onModuleSelected
    this.onModuleUnSelected = onModuleUnSelected
    this.closeContextMenu = closeContextMenu
    this.selectedModule = null
    this.gridSize = gridSize
    this.boxSize = boxSize
    this.svg = d3.select(deviceRef.current).append('svg')
      .attr('height', '100%')
      .attr('width', '100%')
    this.svgGroup = this.svg.append('g')
    this.gridState = new Array(this.gridSize).fill(null).map(() => new Array(this.gridSize).fill(null))
    this.svgWidth = parseInt(this.svg.style('width'), 10)
    this.svgHeight = parseInt(this.svg.style('height'), 10)
    this.maxZoom = maxZoom
    this.defaultZoom = defaultZoom
    this.zoom = this._createZoomHandler()
    this.svg.call(this.zoom)
  }

  private _createZoomHandler() {
    const maxWidthHeight = Math.max(this.svgWidth, this.svgHeight)
    const maxSize = this.gridSize * this.boxSize
    return d3.zoom<any, any>()
      .scaleExtent([maxWidthHeight / (this.gridSize * this.boxSize), this.maxZoom])
      .translateExtent([[0, 0], [maxSize, maxSize]])
      .on('zoom', () => {
        this.closeContextMenu()
        const transofrm = d3.event.transform
        this.svgGroup.attr('transform', `translate(${transofrm.x},${transofrm.y})scale(${transofrm.k})`)
      })
  }

  private _handleKeyCommand = () => {
    const { keyCode } = d3.event
    if (keyCode === 82) {
      this.rotate(true)
    }
    if (keyCode === 46) {
      this.remove()
    }
  }

  private _dragOver (rect: d3.Selection<SVGRectElement, unknown, null, undefined>) {
    rect.style('fill', '#2ecc71')
  }

  private _dragLeave(rect: d3.Selection<SVGRectElement, unknown, null, undefined>) {
    rect.style('fill', 'white')
  }

  private _setCellState (row: number, col: number, module: Module | null) {
    this.gridState[row][col].module = module
  }

  private _setSelectedModule(selectedModule: SelectedElement) {
    this.selectedModule = selectedModule.module
    this.selectedModule.select()
  }

  private _unsetSelectedModule () {
    if (this.selectedModule) this.selectedModule.unselect()
    this.selectedModule = null
  }

  private _reactSetSelectModule(selectedModule: SelectedElement, x: number, y: number, isRotatable: boolean) {
    this._setSelectedModule(selectedModule)
    this.onModuleSelected(x, y, isRotatable)
  }

  private _reactUnsetSelectedModule () {
    this._unsetSelectedModule()
    this.onModuleUnSelected()
  }

  private _removeUnusedSquare(square: d3.Selection<d3.BaseType, unknown, null, undefined>, minRow: number, maxRow: number, minCol: number, maxCol: number) {
    const row = parseInt(square.attr('y'), 10) / this.boxSize
    const col = parseInt(square.attr('x'), 10) / this.boxSize
    const indexOutOfEnclosingSquare = row < minRow || row > maxRow || col < minCol || col > maxCol
    if (indexOutOfEnclosingSquare) square.remove()
    else {
      square.attr('y', row * this.boxSize - minRow * this.boxSize)
      square.attr('x', col * this.boxSize - minCol * this.boxSize)
    }
  }

  private _handleGridElementDragEnd(draggedModule: HTMLElement, startRowPos: number, startColPos: number) {
    const row = Math.floor(d3.event.y / this.boxSize)
    const col = Math.floor(d3.event.x / this.boxSize)
    if (this.gridState[row][col].module) {
      d3.select(draggedModule).attr('x', startColPos * this.boxSize).attr('y', startRowPos * this.boxSize)
    } else {
      this._setCellState(row, col, this.gridState[startRowPos][startColPos].module)
      this._setCellState(startRowPos, startColPos, null)
      d3.select(draggedModule).attr('x', col * this.boxSize).attr('y', row * this.boxSize)
    }

    this._reactUnsetSelectedModule()
    this._dragLeave(this.gridState[row][col].rect)
  }

  private _hanldeElementDrag(draggedModule: HTMLElement, selectedElement: SelectedElement, lastRowPos: number, lastColPos: number) {
    d3.select(draggedModule).attr('x', d3.event.x).attr('y', d3.event.y).raise()
    this._unsetSelectedModule()
    this._setSelectedModule(selectedElement)

    const row = Math.floor(d3.event.y / this.boxSize)
    const col = Math.floor(d3.event.x / this.boxSize)

    if ((lastRowPos !== row || lastColPos !== col) && (lastRowPos >= 0 && lastColPos >= 0)) {
      this._dragLeave(this.gridState[lastRowPos][lastColPos].rect)
    }

    if (row >= 0 && col >= 0 && !this.gridState[row][col].module) {
      this._dragOver(this.gridState[row][col].rect)
      return { row, col }
    }
    return null
  }

  private async _handleElementClick(selectedElement: SelectedElement) {
    const isRotatable = selectedElement.module.isRotatable()
    this._reactUnsetSelectedModule()
    const scale = parseFloat(Module.GetTokenizedTransformAttributeValue(this.svgGroup.attr('transform')).scale[0])
    const {
      SelectedModuleContextMenuX,
      SelectedModuleContextMenuY,
    } = await this.getContextMenuPosition(scale, selectedElement)

    this._reactSetSelectModule(
      selectedElement,
      SelectedModuleContextMenuX,
      SelectedModuleContextMenuY,
      isRotatable,
    )
  }

  private async _drop(square: SVGRectElement, gridElement: GridElement) {
    d3.event.preventDefault()

    const self = this
    let startRowPos = -1
    let startColPos = -1
    let lastRowPos = -1
    let lastColPos = -1

    const moduleCategory = String(d3.event.dataTransfer.getData('category'))
    const moduleName = String(d3.event.dataTransfer.getData('name'))

    if (!ModuleUtils.moduleExists(moduleName, moduleCategory)) return
    if (self.gridState[gridElement.row][gridElement.col].module) return //position is already occupied

    const droppedModule = ModuleUtils.buildModule(moduleName, moduleCategory)
    const svgModule = await droppedModule.XMLDocument()
    const strOriginalSize = svgModule.attributes.getNamedItem('height')?.value ?? String(self.boxSize)
    const originalSize = parseFloat(strOriginalSize)
    self._dragLeave(d3.select(square))
    self._reactUnsetSelectedModule()
    self.svgGroup.node().appendChild(svgModule)

    self._setCellState(gridElement.row, gridElement.col, droppedModule)

    d3.select(svgModule)
      .data([{ module: droppedModule, gridElement }])
      .attr('x', gridElement.xPos)
      .attr('y', gridElement.yPos)
      .call(d3.drag<HTMLElement, any>()
        .on('start', () => {
          startRowPos = Math.floor(d3.event.y / self.boxSize)
          startColPos = Math.floor(d3.event.x / self.boxSize)
        })

        .on('drag', function (selectedElement: SelectedElement) {
          const data = self._hanldeElementDrag(this, selectedElement, lastRowPos, lastColPos)
          if (data) {
            lastRowPos = data.row
            lastColPos = data.col
          }
        })

        .on('end', function () {
          self._handleGridElementDragEnd(this, startRowPos, startColPos)
        }))
      .select('g')
      .attr('transform', `scale(${(self.boxSize - 1) / originalSize})`)
      .on('click', (selectedElement: SelectedElement) => {
        self._handleElementClick(selectedElement)
      })

  }

  public getContextMenuPosition = async (scale: number, selectedElement: SelectedElement) => {
    const { pageX, pageY } = d3.event
    const coordinates = d3.mouse(this.svgGroup.node() as any)
    const xml = await selectedElement.module.XMLDocument()
    const x = xml.attributes.getNamedItem('x') ? parseFloat((xml.attributes.getNamedItem('x') as any).value) : 0
    const y = xml.attributes.getNamedItem('y') ? parseFloat((xml.attributes.getNamedItem('y') as any).value) : 0
    const dx = (coordinates[0] - x) * scale
    const dy = (coordinates[1] - y) * scale

    return {
      SelectedModuleContextMenuX: pageX - dx - 20,
      SelectedModuleContextMenuY: pageY - dy - 72,
    }
  }

  public centerAndRescaleGrid = () => {
    const boxCenterX = this.svgWidth / 2
    const boxCenterY = this.svgHeight / 2
    const boxCenter = (this.gridSize * this.boxSize) / 2
    this.zoom.translateBy(this.svg, boxCenterX - boxCenter, boxCenterY - boxCenter)
    this.zoom.scaleBy(this.svg, this.defaultZoom)
  }

  public zoomBy(percent: number) {
    this.zoom.scaleBy(this.svg, percent)
  }

  public updateDimensions = () => {
    this.svgWidth = parseInt(this.svg.style('width'), 10)
    this.svgHeight = parseInt(this.svg.style('height'), 10)
    this.zoom = this._createZoomHandler()
    this.svg.call(this.zoom)
    this.zoomBy(this.defaultZoom)
  }

  public drawGrid() {
    const self = this
    const gridData = ConfiguratorGridUtils.gridDataGenerator(this.boxSize, this.gridSize)
    this.svgGroup.selectAll('.square')
      .data(gridData)
      .enter()
      .append('rect')
      .attr('x', (gridElement: GridElement) => gridElement.xPos)
      .attr('y', (gridElement: GridElement) => gridElement.yPos)
      .attr('width', self.boxSize)
      .attr('height', self.boxSize)
      .attr('class', 'square')
      .style('fill', '#fff')
      .style('stroke', '#222')

      .on('dragover', function () {
        d3.event.preventDefault()
        self._dragOver(d3.select(this))
      })

      .on('dragleave', function () {
        self._dragLeave(d3.select(this))
      })

      .on('dragenter', () => {
        d3.event.preventDefault()
      })

      .on('drop', function (gridElement: GridElement) {
        self._drop(this, gridElement)
      })

      .on('click', () => {
        if (self.selectedModule) {
          self._reactUnsetSelectedModule()
        }
      })

      .each((gridElement: GridElement, idx: number, rects: SVGRectElement[] | d3.ArrayLike<SVGRectElement>) => {
        self.gridState[gridElement.row][gridElement.col] = {
          module: null,
          rect: d3.select(rects[idx]),
        }
      })

    d3.select('body').on('keydown', this._handleKeyCommand)
  }

  public async rotate(direction: boolean) {
    if (!this.selectedModule) return
    const selectedModule = await this.selectedModule.XMLDocument()
    const width = selectedModule.attributes.getNamedItem('width')
    const height = selectedModule.attributes.getNamedItem('height')
    if (!width || !height) return
    this.selectedModule.rotate(Math.floor(parseFloat(width.value)), Math.floor(parseFloat(height.value)), direction)
  }

  public async remove() {
    if (!this.selectedModule) return
    const selectedModule = await this.selectedModule.XMLDocument()
    const x = selectedModule.attributes.getNamedItem('x')
    const y = selectedModule.attributes.getNamedItem('y')
    if (!x || !y) return
    const row = Math.floor(parseFloat(y.value) / this.boxSize)
    const col = Math.floor(parseFloat(x.value) / this.boxSize)
    this._setCellState(row, col, null)
    this.selectedModule.remove()
    this._reactUnsetSelectedModule()
  }

  async getDevice(): Promise<DeviceData> {
    const serializer = new XMLSerializer()
    const { minRow, maxRow, minCol, maxCol } = ConfiguratorGridUtils.getEnclosingSquare(this.gridState)
    const copy = this.svg.clone(true)
    const self = this
    copy.selectAll('.square').each((a, idx, squares) => {
      self._removeUnusedSquare(d3.select(squares[idx]), minRow, maxRow, minCol, maxCol)
    })
    copy.select('g').attr('transform', 'translate(0)scale(1)')

    copy.selectAll('svg').each((a, idx, squares) => {
      self._removeUnusedSquare(d3.select(squares[idx]), minRow, maxRow, minCol, maxCol)
    })

    const xmlString = serializer.serializeToString(copy.node() as any)
    copy.remove()
    const imageData = await ConfiguratorGridUtils.svgToPng(xmlString, (maxCol - minCol + 1) * this.boxSize, (maxRow - minRow + 1) * this.boxSize)
    return { imageData, configuration: {} }
  }

  public showEntireDevice() {
    const { minRow, maxRow, minCol, maxCol } = ConfiguratorGridUtils.getEnclosingSquare(this.gridState)
    if (!ConfiguratorGridUtils.indexIsValid(minRow)) return
    if ((maxRow - minRow) + (maxCol - minCol) !== 0) {
      const maxLength = Math.max((maxRow - minRow + 2), (maxCol - minCol + 2)) * this.boxSize
      const maxWH = Math.max(this.svgWidth, this.svgHeight)
      const scaleBy = maxWH / maxLength

      const { leftPos, topPos } = ConfiguratorGridUtils.getTranslation(this.gridSize, this.boxSize, scaleBy, this.svgHeight, this.svgWidth, minRow, minCol)

      this.svg.call(this.zoom).call(this.zoom.transform, d3.zoomIdentity.translate(-leftPos, -topPos).scale(scaleBy))
    } else {
      this.svg.call(this.zoom).call(this.zoom.transform, d3.zoomIdentity.translate(-minCol * this.boxSize, -minRow * this.boxSize).scale(1))
    }
  }
}
